import { useEffect } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Building2,
  Download,
  GraduationCap,
  PenLine,
  ShieldCheck,
  UserCog,
  type LucideIcon,
} from 'lucide-react';
import { MarkdownContent } from '@/components/course/markdown';
import { SearchBox } from '@/components/catalog';
import { VideoCard, WatchLink } from '@/components/video';
import { VIDEOS, searchVideos, videosFor } from '@/content/videos';
import {
  AUDIENCE_LABELS,
  GUIDES,
  findGuide,
  guidePath,
  isOrganizationGuide,
  searchGuides,
  type Guide,
  type GuideAudience,
} from '@/content/guides';

/*
 * Resources (content/guides, content/videos): public, for learners, authors,
 * admins and anyone considering the platform. /resources is the hub (search
 * across guides and videos, where to start, videos, guides), /guides lists every guide, /guides/:guide shows one's
 * contents and /guides/:guide/:chapter a chapter. /guides/:guide/print is the
 * whole guide on one page, which `scripts/render-guide-pdf.sh` prints to its
 * PDF. The first guide lived at /guide; those links redirect.
 */

const downloadLink =
  'inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius)] border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted sm:w-auto';
const cardLink =
  'flex h-full gap-4 rounded-xl border border-border bg-card p-4 hover:border-primary';
const eyebrow =
  'flex items-center gap-2 text-xs font-semibold tracking-wider text-primary uppercase';

/** "October 24, 2026" from a YYYY-MM-DD review date, whatever the viewer's zone. */
function formatReviewed(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y!, m! - 1, d).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

const START_HERE: { icon: LucideIcon; who: string; text: string; to: string; cta: string }[] = [
  {
    icon: GraduationCap,
    who: 'Learners',
    text: 'Find your starting point, then take courses, quizzes and workshops and share your certificates.',
    to: guidePath('learners'),
    cta: 'The learner guide',
  },
  {
    icon: PenLine,
    who: 'Course authors',
    text: 'Build courses and quizzes, draft with AI, run live events and keep them up to date.',
    to: guidePath('authors'),
    cta: 'The author guide',
  },
  {
    icon: UserCog,
    who: 'Admins',
    text: 'Bring people in, assign courses, follow progress and answer your members.',
    to: guidePath('admins'),
    cta: 'The admin guide',
  },
  {
    icon: Building2,
    who: 'Organizations',
    text: 'Everything an organization can do, from sign-up to its own courses and workshops.',
    to: guidePath('organizations'),
    cta: 'The organization guide',
  },
];

function GuideCard({ guide }: { guide: Guide }) {
  return (
    <Link to={guidePath(guide.slug)} className={cardLink}>
      <BookOpen className="mt-0.5 size-6 shrink-0 text-primary" aria-hidden />
      <span className="space-y-1">
        <span className="block font-semibold">{guide.title}</span>
        <span className="block text-sm text-muted-foreground">{guide.subtitle}</span>
        <span className="block text-xs text-muted-foreground">
          {guide.chapters.length} chapters
          {guide.pdf && ' · PDF'} · For{' '}
          {guide.audiences.map((a) => AUDIENCE_LABELS[a].toLowerCase()).join(', ')}
        </span>
      </span>
    </Link>
  );
}

export function ResourcesPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const hits = searchGuides(q);
  const videoHits = searchVideos(q);
  const found = hits.length + videoHits.length;
  return (
    <div className="space-y-10">
      <header className="max-w-3xl space-y-3">
        <p className={eyebrow}>
          <BookOpen className="size-4" aria-hidden /> Resources
        </p>
        <h1 className="font-display text-3xl font-semibold">Help and resources</h1>
        <p className="text-lg text-muted-foreground">
          Guides and video tutorials for learners, course authors, admins and organizations.
        </p>
        <div className="pt-2">
          <SearchBox
            value={q}
            onChange={(v) => setParams(v ? { q: v } : {}, { replace: true })}
            placeholder="Search guides and videos"
            label="Search guides and videos"
          />
        </div>
      </header>

      {q.trim() ? (
        <section className="space-y-3" aria-live="polite">
          <h2 className="text-sm font-semibold text-muted-foreground">
            {found ? `${found} ${found === 1 ? 'result' : 'results'}` : 'No results'}
          </h2>
          {videoHits.length > 0 && (
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {videoHits.map((v) => (
                <li key={v.slug}>
                  <VideoCard video={v} />
                </li>
              ))}
            </ul>
          )}
          {hits.length > 0 && (
            <ul className="space-y-3">
              {hits.map((h) => (
                <li key={`${h.guide.slug}/${h.chapter.slug}`}>
                  <Link
                    to={guidePath(h.guide.slug, h.chapter.slug)}
                    className="block rounded-xl border border-border bg-card p-4 hover:border-primary"
                  >
                    <span className="block text-xs text-muted-foreground">{h.guide.title}</span>
                    <span className="block font-semibold">{h.chapter.title}</span>
                    <span className="mt-1 block text-sm text-muted-foreground">{h.excerpt}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {!found && (
            <p className="text-sm text-muted-foreground">
              Try other words, or{' '}
              <Link to="/guides" className="-my-2 py-2 font-semibold text-primary">
                browse the guides
              </Link>
              .
            </p>
          )}
        </section>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="font-display text-xl font-semibold">Start here</h2>
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {START_HERE.map((s) => (
                <li key={s.who}>
                  <Link
                    to={s.to}
                    className="flex h-full flex-col gap-2 rounded-xl border border-border bg-card p-4 hover:border-primary"
                  >
                    <s.icon className="size-6 text-primary" aria-hidden />
                    <span className="font-semibold">{s.who}</span>
                    <span className="flex-1 text-sm text-muted-foreground">{s.text}</span>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
                      {s.cta} <ArrowRight className="size-4" aria-hidden />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
          {VIDEOS.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-display text-xl font-semibold">Videos</h2>
                <Link to="/videos" className="-my-2 py-2 text-sm font-semibold text-primary">
                  See all
                </Link>
              </div>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {VIDEOS.slice(0, 3).map((v) => (
                  <li key={v.slug}>
                    <VideoCard video={v} />
                  </li>
                ))}
              </ul>
            </section>
          )}
          {[
            { title: 'For your kind of organization', guides: GUIDES.filter(isOrganizationGuide) },
            { title: 'For your role', guides: GUIDES.filter((g) => !isOrganizationGuide(g)) },
          ].map((group) => (
            <section key={group.title} className="space-y-3">
              <h2 className="font-display text-xl font-semibold">{group.title}</h2>
              <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {group.guides.map((g) => (
                  <li key={g.slug}>
                    <GuideCard guide={g} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

export function GuidesPage() {
  const [params, setParams] = useSearchParams();
  const audiences = [...new Set(GUIDES.flatMap((g) => g.audiences))];
  const raw = params.get('for');
  const audience = audiences.find((a) => a === raw) as GuideAudience | undefined;
  const shown = audience ? GUIDES.filter((g) => g.audiences.includes(audience)) : GUIDES;
  const chip = (active: boolean) =>
    `min-h-10 shrink-0 rounded-full border px-4 text-sm ${active ? 'border-primary bg-primary/10 font-semibold text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`;
  return (
    <div className="space-y-6">
      <Link
        to="/resources"
        className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        ← Resources
      </Link>
      <header className="max-w-3xl space-y-2">
        <h1 className="font-display text-3xl font-semibold">Guides</h1>
        <p className="text-muted-foreground">
          Step-by-step guides to Nirvik Ready, each also as a PDF.
        </p>
      </header>
      {GUIDES.length > 1 && (
        <div
          role="group"
          aria-label="Who it’s for"
          className="-mx-4 flex gap-2 overflow-x-auto px-4"
        >
          <button type="button" className={chip(!audience)} onClick={() => setParams({})}>
            All
          </button>
          {audiences.map((a) => (
            <button
              key={a}
              type="button"
              aria-pressed={audience === a}
              className={chip(audience === a)}
              onClick={() => setParams({ for: a })}
            >
              {AUDIENCE_LABELS[a]}
            </button>
          ))}
        </div>
      )}
      <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {shown.map((g) => (
          <li key={g.slug}>
            <GuideCard guide={g} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function GuidePage() {
  const guide = findGuide(useParams().guide);
  if (!guide) return <Navigate to="/guides" replace />;
  return (
    <div className="space-y-8">
      <header className="max-w-3xl space-y-3">
        <Link
          to="/guides"
          className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          ← Guides
        </Link>
        <p className={eyebrow}>
          <BookOpen className="size-4" aria-hidden /> Guide
        </p>
        <h1 className="font-display text-3xl font-semibold">{guide.title}</h1>
        <p className="text-lg text-muted-foreground">{guide.subtitle}</p>
        <div className="flex flex-col gap-3 pt-2 sm:flex-row">
          <Link
            to={guidePath(guide.slug, guide.chapters[0]!.slug)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius)] bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 sm:w-auto"
          >
            Start reading <ArrowRight className="size-4" aria-hidden />
          </Link>
          {guide.pdf && (
            <a href={guide.pdf} download className={downloadLink}>
              <Download className="size-4" aria-hidden /> Download the PDF
            </a>
          )}
        </div>
      </header>
      <ol className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {guide.chapters.map((c, i) => (
          <li key={c.slug}>
            <Link to={guidePath(guide.slug, c.slug)} className={cardLink}>
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border text-sm font-semibold text-muted-foreground">
                {i}
              </span>
              <span>
                <span className="block font-semibold">{c.title}</span>
                <span className="block text-sm text-muted-foreground">{c.summary}</span>
              </span>
            </Link>
          </li>
        ))}
      </ol>
      {isOrganizationGuide(guide) && (
        <p className="text-sm text-muted-foreground">
          Ready to start?{' '}
          <Link to="/signin" className="-my-2 py-2 font-semibold text-primary">
            Sign in
          </Link>{' '}
          and choose <span className="font-medium text-foreground">Request an organization</span> in
          the sidebar.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Last reviewed {formatReviewed(guide.reviewed)}
      </p>
    </div>
  );
}

export function GuideChapterPage() {
  const params = useParams();
  const guide = findGuide(params.guide);
  const index = guide ? guide.chapters.findIndex((c) => c.slug === params.chapter) : -1;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [params.guide, params.chapter]);
  if (!guide) return <Navigate to="/guides" replace />;
  if (index < 0) return <Navigate to={guidePath(guide.slug)} replace />;
  const c = guide.chapters[index]!;
  const prev = guide.chapters[index - 1];
  const next = guide.chapters[index + 1];
  return (
    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_16rem]">
      <article className="min-w-0 space-y-6">
        <Link
          to={guidePath(guide.slug)}
          className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          ← {guide.title}
        </Link>
        {videosFor(guide.slug, c.slug).map((v) => (
          <WatchLink key={v.slug} video={v} />
        ))}
        <MarkdownContent markdown={c.markdown} />
        <p className="text-xs text-muted-foreground">
          Last reviewed {formatReviewed(c.reviewed ?? guide.reviewed)}
        </p>
        <nav className="flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:justify-between">
          {prev ? (
            <Link
              to={guidePath(guide.slug, prev.slug)}
              className="-my-2 inline-flex items-center gap-2 py-2 text-sm font-semibold text-primary"
            >
              <ArrowLeft className="size-4" aria-hidden /> {prev.title}
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link
              to={guidePath(guide.slug, next.slug)}
              className="-my-2 inline-flex items-center gap-2 py-2 text-sm font-semibold text-primary"
            >
              {next.title} <ArrowRight className="size-4" aria-hidden />
            </Link>
          )}
        </nav>
      </article>
      <aside className="hidden space-y-3 lg:sticky lg:top-6 lg:block">
        <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
          Contents
        </p>
        <ol className="space-y-1 text-sm">
          {guide.chapters.map((g) => (
            <li key={g.slug}>
              <Link
                to={guidePath(guide.slug, g.slug)}
                className={
                  g.slug === c.slug
                    ? 'font-semibold text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }
              >
                {g.title}
              </Link>
            </li>
          ))}
        </ol>
        {guide.pdf && (
          <a href={guide.pdf} download className={downloadLink}>
            <Download className="size-4" aria-hidden /> Download the PDF
          </a>
        )}
      </aside>
    </div>
  );
}

/** Every chapter of a guide on one page, laid out for printing to its PDF. */
export function GuidePrintPage() {
  const guide = findGuide(useParams().guide);
  if (!guide) return <Navigate to="/guides" replace />;
  return (
    <div className="guide-print mx-auto max-w-3xl px-8 py-10 text-foreground">
      <section className="flex min-h-[90vh] flex-col justify-center break-after-page">
        <p className="flex items-center gap-2 font-semibold text-primary">
          <ShieldCheck className="size-6" aria-hidden /> Nirvik Ready
        </p>
        <h1 className="mt-6 font-display text-4xl font-semibold">{guide.title}</h1>
        <p className="mt-4 text-lg text-muted-foreground">{guide.subtitle}</p>
        <p className="mt-10 text-sm text-muted-foreground">
          ready.nirvik.ai{guidePath(guide.slug)} · A Nirvik AI Labs project · Reviewed{' '}
          {formatReviewed(guide.reviewed)}
        </p>
      </section>
      <section className="break-after-page">
        <h2 className="font-display text-2xl font-semibold">Contents</h2>
        <ol className="mt-4 space-y-2">
          {guide.chapters.map((c, i) => (
            <li key={c.slug} className="flex gap-3">
              <span className="w-6 text-muted-foreground">{i}</span>
              <span>
                <span className="font-semibold">{c.title}</span>
                <span className="text-muted-foreground"> · {c.summary}</span>
              </span>
            </li>
          ))}
        </ol>
      </section>
      {guide.chapters.map((c) => (
        <section key={c.slug} className="break-before-page">
          <MarkdownContent markdown={c.markdown} />
        </section>
      ))}
    </div>
  );
}

/** The first guide's old address, /guide and /guide/:chapter. */
export function LegacyGuideRedirect() {
  const { chapter } = useParams();
  return <Navigate to={guidePath('organizations', chapter)} replace />;
}
